/* eslint-disable no-console */
/**
 * Flag a prop as deprecated
 *
 * propTypes = {
 *   someProp: customProps.deprecated('`someProp` is deprecated, please use `someOtherProp` instead.', ['allowed-value'])
 * }
 *
 * @param {String} description   A description to log in the console
 * @param {Array}  allowedValues An array of allowed values for the default prop (this allows prop defaults to not warn)
 */

const isDev = process.env.NODE_ENV !== 'production';

// Make sure we're in an environment that can use the console
const canUseConsole = () =>
  typeof console !== 'undefined' &&
  typeof console.log === 'function' &&
  typeof console.groupCollapsed === 'function' &&
  typeof console.groupEnd === 'function';

const delayCallback = (cb) => {
  typeof window !== 'undefined' &&
  typeof window.requestIdleCallback === 'function'
    ? window.requestIdleCallback(cb)
    : setTimeout(cb, 5000);
};

const isStyleWrapper = (componentName) =>
  componentName.includes('@StyleWrapper');

const deprecationLogOnce = (() => {
  const logs = [];
  const propNameCounts = {};
  const processProp = ({propName, componentName, propValue, description}) => {
    if (propNameCounts[propName] && propNameCounts[propName].length > 0) {
      const maybeValue = propNameCounts[propName].find(
        (value) => value === propValue
      );
      if (typeof maybeValue === 'undefined') {
        propNameCounts[propName].push(propValue);
      }
    } else {
      propNameCounts[propName] = [propValue];
      logs.push({
        component: componentName,
        prop: propName,
        value: propValue,
        desc: description,
      });
    }
  };

  const groupHeadingCSS = 'color: red; font-size: 16px;';
  const propNameCSS = 'font-weight: bold; color: red;';
  const descriptionCSS =
    'padding-left: 15px; color: gray; font-weight: lighter;';
  const currentValueCSS = 'padding-left: 15px;';

  if (canUseConsole()) {
    delayCallback(() => {
      if (logs.length > 0) {
        console.groupCollapsed(
          `%c ⚠️ ${logs.length} deprecated prop${
            logs.length > 1 ? 's' : ''
          } in use ⚠️`,
          groupHeadingCSS
        );

        logs.forEach((log) => {
          console.log(
            `%c • Component: ${log.component}, Prop: ${log.prop} (${
              propNameCounts[log.prop].length
            } instance${propNameCounts[log.prop].length > 1 ? 's' : ''})`,
            propNameCSS
          );
          console.log(`%c 🗣️ ${log.desc}`, descriptionCSS);
          console.groupCollapsed(`%c 🙅 Values:`, currentValueCSS);
          propNameCounts[log.prop].forEach((value) => {
            console.log(
              `%c '${typeof value === 'function' ? value.name : value}'`,
              currentValueCSS
            );
          });
          console.groupEnd();
        });
        console.groupEnd();
      }
    });
  }

  return processProp;
})();

/**
 * Flag a prop as deprecated
 *
 * propTypes = {
 *   someProp: customProps.deprecated('`someProp` is deprecated, please use `someOtherProp` instead.', ['allowed-value'])
 * }
 *
 * @param {String} description   A description to log in the console
 * @param {Array}  allowedValues An array of allowed values for the default prop (this allows prop defaults to not warn)
 */
const deprecatedProp = (description, allowedValues = []) => (
  props,
  propName,
  componentName
) => {
  const allAllowedValues = [...allowedValues, null];
  // Ignore StyleWrapper since it contains all the props of it's children.
  // If the deprecated prop isn't actually being set, or is set to null (typically via defaultProps) move along
  if (
    allAllowedValues.includes(props[propName]) ||
    typeof props[propName] === 'undefined' ||
    isStyleWrapper(componentName) ||
    isDev
  ) {
    return false;
  }

  // Error if no description
  if (typeof description !== 'string' || !description) {
    throw new Error(`
          Invalid \`description\` argument supplied to \`deprecated\`, expected a string.
          See \`${propName}\` propTypes definition in the \`${componentName}\` component.
        `);
  }

  deprecationLogOnce({
    propName,
    componentName,
    propValue: props[propName],
    description,
  });

  return null;
};

export default deprecatedProp;
